if (!customElements.get('bulk-order-configurator')) {
  class BulkOrderConfigurator extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      const payloadEl = this.querySelector('script[data-variants]');
      if (!payloadEl) return;

      try {
        this.variants = JSON.parse(payloadEl.textContent);
      } catch (e) {
        console.error('bulk-order-configurator: invalid JSON payload', e);
        return;
      }
      if (!Array.isArray(this.variants) || this.variants.length === 0) return;

      this.selections = {};
      this.cartQtys = {};
      this.discountActive = false;
      this.variants.forEach((v) => { this.selections[v.variantId] = 0; });

      this.rows = this.querySelectorAll('[data-row]');
      this.totalEl = this.querySelector('[data-bulk-order-total]');
      this.addBtn = this.querySelector('[data-bulk-order-add]');
      this.errorEl = this.querySelector('[data-bulk-order-error]');

      this.bindRows();
      this.bindAdd();
      this.refreshCart().then(() => this.recomputeAll());
      this.detectDiscountPricing();
    }

    // A pricing app (e.g. WPD wholesale) rewrites the displayed price in the DOM
    // asynchronously after load. Poll for up to 3s: if a discounted price appears,
    // switch the total to use the DOM prices; otherwise fall back to standard prices.
    detectDiscountPricing() {
      const DEADLINE_MS = 3000;
      const INTERVAL_MS = 250;
      let waited = 0;

      const check = () => {
        if (this.discountDetected()) {
          this.discountActive = true;
          console.log('Bulk Order Configurator: discount price activated — totals use discounted prices.');
          this.recomputeTotal();
          this.watchDiscountChanges();
          return;
        }

        waited += INTERVAL_MS;
        if (waited >= DEADLINE_MS) {
          this.discountActive = false;
          console.log('Bulk Order Configurator: standard pricing (no discount detected).');
          this.recomputeTotal();
          this.watchDiscountChanges();
          return;
        }

        setTimeout(check, INTERVAL_MS);
      };

      check();
    }

    // The pricing app can be slow/flaky (esp. on poor networks) and may apply or
    // change the discount after the initial detection window. Re-check every 5s
    // for up to 30s and re-apply if the discount state (or amount) has changed.
    watchDiscountChanges() {
      const INTERVAL_MS = 5000;
      const DURATION_MS = 30000;
      let elapsed = 0;

      const tick = () => {
        elapsed += INTERVAL_MS;
        const active = this.discountDetected();

        if (active !== this.discountActive) {
          this.discountActive = active;
          console.log(active
            ? 'Bulk Order Configurator: discount appeared on re-check — totals updated to discounted prices.'
            : 'Bulk Order Configurator: discount no longer present on re-check — reverted to standard pricing.');
          this.recomputeTotal();
        } else if (active) {
          // Still discounted — refresh in case the app changed the amount.
          this.recomputeTotal();
        }

        if (elapsed < DURATION_MS) setTimeout(tick, INTERVAL_MS);
      };

      setTimeout(tick, INTERVAL_MS);
    }

    // A discount is active if the app injected its struck compare-at price, or if
    // any rendered price differs from the base variant price in the JSON payload.
    discountDetected() {
      if (this.querySelector('.bulk-order-configurator__price .wpd-ext-compare-at-price')) return true;

      return Array.from(this.rows).some((row) => {
        const v = this.variants.find((x) => x.variantId === Number(row.dataset.variantId));
        if (!v) return false;
        const priceEl = row.querySelector('.bulk-order-configurator__price');
        const domCents = BulkOrderConfigurator.parsePriceToCents(priceEl);
        return domCents != null && domCents !== (v.price || 0);
      });
    }

    bindRows() {
      this.rows.forEach((row) => {
        const minus = row.querySelector('[data-qty-minus]');
        const plus = row.querySelector('[data-qty-plus]');
        const input = row.querySelector('[data-qty-input]');
        const clear = row.querySelector('[data-qty-clear]');

        minus?.addEventListener('click', () => this.changeQty(row, -1));
        plus?.addEventListener('click', () => this.changeQty(row, 1));
        input?.addEventListener('input', () => this.onInputChange(row));
        input?.addEventListener('change', () => this.onInputChange(row));
        clear?.addEventListener('click', () => this.setRowQty(row, 0));
      });
    }

    bindAdd() {
      this.addBtn?.addEventListener('click', () => this.handleAdd());
    }

    async refreshCart() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`);
        if (!res.ok) return;
        const data = await res.json();
        this.cartQtys = {};
        (data.items || []).forEach((line) => {
          this.cartQtys[line.variant_id] = (this.cartQtys[line.variant_id] || 0) + line.quantity;
        });
      } catch (e) {
        this.cartQtys = {};
      }
    }

    changeQty(row, direction) {
      const mult = Math.max(1, parseInt(row.dataset.multiplier, 10) || 1);
      const input = row.querySelector('[data-qty-input]');
      const current = parseInt(input.value, 10) || 0;
      this.setRowQty(row, current + (direction * mult));
    }

    onInputChange(row) {
      const input = row.querySelector('[data-qty-input]');
      const current = parseInt(input.value, 10);
      this.setRowQty(row, isNaN(current) ? 0 : current);
    }

    setRowQty(row, qty) {
      const variantId = Number(row.dataset.variantId);
      const tracks = row.dataset.tracksInventory === 'true';
      const inventoryQty = parseInt(row.dataset.inventoryQuantity, 10) || 0;
      const cartQty = this.cartQtys[variantId] || 0;
      const mult = Math.max(1, parseInt(row.dataset.multiplier, 10) || 1);
      const available = tracks ? Math.max(0, inventoryQty - cartQty) : Infinity;

      // Snap the requested qty up to the next whole multiple of the pack size.
      let next = Math.ceil(Math.max(0, qty) / mult) * mult;

      // Cap at the largest whole pack that fits in available stock (done silently).
      if (tracks) {
        const maxBoxes = Math.floor(available / mult) * mult;
        if (next > maxBoxes) next = maxBoxes;
      }

      const input = row.querySelector('[data-qty-input]');
      input.value = next;
      this.selections[variantId] = next;

      this.updateRowState(row, { tracks, inventoryQty, cartQty, selection: next });
      this.recomputeTotal();
      this.updateAddBtnState();
    }

    updateRowState(row, { tracks, inventoryQty, cartQty, selection }) {
      const minus = row.querySelector('[data-qty-minus]');
      const plus = row.querySelector('[data-qty-plus]');
      const input = row.querySelector('[data-qty-input]');
      const badge = row.querySelector('[data-stock-badge]');
      const rowError = row.querySelector('[data-row-error]');
      const mult = Math.max(1, parseInt(row.dataset.multiplier, 10) || 1);
      const available = Math.max(0, inventoryQty - cartQty);
      const maxBoxes = Math.floor(available / mult) * mult;

      let state = 'ok';
      let badgeText = 'In stock';

      // Out of stock when no whole pack fits the remaining stock
      // (covers both zero inventory and "less than one pack left").
      if (tracks && maxBoxes <= 0) {
        state = 'out';
        badgeText = 'Out of stock';
      }

      row.dataset.stockState = state;
      if (badge) badge.textContent = badgeText;

      if (rowError) {
        rowError.hidden = true;
        rowError.textContent = '';
      }

      const disable = state === 'out';
      [minus, plus, input].forEach((el) => { if (el) el.disabled = disable; });

      // No whole pack fits the remaining stock -> block adding more.
      if (plus && tracks && !disable) {
        plus.disabled = selection >= maxBoxes;
      }
    }

    recomputeAll() {
      this.rows.forEach((row) => {
        const variantId = Number(row.dataset.variantId);
        const selection = this.selections[variantId] || 0;
        const tracks = row.dataset.tracksInventory === 'true';
        const inventoryQty = parseInt(row.dataset.inventoryQuantity, 10) || 0;
        const cartQty = this.cartQtys[variantId] || 0;
        this.updateRowState(row, { tracks, inventoryQty, cartQty, selection });
      });
      this.recomputeTotal();
      this.updateAddBtnState();
    }

    recomputeTotal() {
      if (!this.totalEl) return;
      let totalCents = 0;
      this.rows.forEach((row) => {
        const variantId = Number(row.dataset.variantId);
        const qty = this.selections[variantId] || 0;
        if (qty > 0) totalCents += qty * this.unitPriceCents(row, variantId);
      });
      this.totalEl.textContent = BulkOrderConfigurator.formatMoney(totalCents);
    }

    // Effective unit price in cents. Prefers the price shown in the DOM so that
    // discounts injected by pricing apps (e.g. WPD wholesale) are reflected in the
    // total. Falls back to the JSON payload price when no rendered price is found.
    unitPriceCents(row, variantId) {
      const v = this.variants.find((x) => x.variantId === variantId);
      const base = v ? (v.price || 0) : 0;

      // Only trust the DOM price once a discount has actually been detected.
      if (!this.discountActive) return base;

      const priceEl = row.querySelector('.bulk-order-configurator__price');
      const domCents = BulkOrderConfigurator.parsePriceToCents(priceEl);
      return domCents != null ? domCents : base;
    }

    static parsePriceToCents(el) {
      if (!el) return null;
      // Ignore any struck "compare at" price the app injects; keep only the effective price.
      const clone = el.cloneNode(true);
      clone.querySelectorAll('.wpd-ext-compare-at-price, s, del').forEach((n) => n.remove());
      const digits = clone.textContent.replace(/[^0-9.]/g, '');
      if (!digits) return null;
      const value = parseFloat(digits);
      return isNaN(value) ? null : Math.round(value * 100);
    }

    updateAddBtnState() {
      if (!this.addBtn) return;
      const totalQty = Object.values(this.selections).reduce((sum, q) => sum + q, 0);
      this.addBtn.disabled = totalQty <= 0;
    }

    async handleAdd() {
      if (this.addBtn.disabled) return;

      const items = Object.entries(this.selections)
          .filter(([, qty]) => qty > 0)
          .map(([id, qty]) => ({ id: Number(id), quantity: qty }));
      if (items.length === 0) return;

      this.setErrorState(null);
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;

      const cartDrawer = document.querySelector('cart-drawer');
      const quickAddDrawer = this.closest('quick-add-drawer');
      const formData = new FormData();
      items.forEach((it, i) => {
        formData.append(`items[${i}][id]`, it.id);
        formData.append(`items[${i}][quantity]`, it.quantity);
      });

      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        const cartDrawerSectionId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (cartDrawerSectionId) sections += `,${cartDrawerSectionId}`;
      }
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();

        if (!res.ok) {
          const msg = data.description || data.message || 'Could not add to cart.';
          this.setErrorState(msg);
          throw new Error(msg);
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (quickAddDrawer && typeof quickAddDrawer.addedToCart === 'function') {
          quickAddDrawer.addedToCart();
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, !quickAddDrawer);
        }

        this.variants.forEach((v) => { this.selections[v.variantId] = 0; });
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-qty-input]');
          if (input) input.value = 0;
        });
        await this.refreshCart();
        this.recomputeAll();
      } catch (err) {
        console.log('bulk-order-configurator add failed:', err); // eslint-disable-line
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.updateAddBtnState();
      }
    }

    setErrorState(msg) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      this.errorEl.textContent = msg || '';
    }

    static formatMoney(cents) {
      const format = (window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }
  }

  customElements.define('bulk-order-configurator', BulkOrderConfigurator);
}
